import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MyClientsSection } from './MyClientsSection';
import { LongevityAssessmentsSection } from './components/longevity/LongevityAssessmentsSection';
import { useLongevityFlag } from './longevityFlag';
import { MyNetworkSection } from './MyNetworkSection';
import { MyResourcesSection } from './MyResourcesSection';
import { InviteClientModal } from './InviteClientModal';
import { MarketingResourcesCard } from './MarketingResourcesCard';
import { CareConciergeCard } from './components/care-concierge/CareConciergeCard';
import { CareConciergeCases } from './components/care-concierge/CareConciergeCases';
import { ShareDialog } from '@/features/resource-hub/components/ShareDialog';
import { useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsTrigger } from '@/shared/components/ui/tabs';
import { ScrollableTabsList } from '@/shared/components/ui/scrollable-tabs-list';
import { CostCalculatorTab } from './components/CostCalculatorTab';
import { CuratedProvidersCard } from './components/CuratedProvidersCard';
import { CuratedResourcesCard } from './components/CuratedResourcesCard';
import { ProfileTabContent } from './components/profile/ProfileTabContent';
import { PermissionGate } from '@/features/workspace-sub-delegation';
import { QATab } from './components/qa-tab/QATab';
import type { FeaturedResource } from './index';

/**
 * Financial Advisor Dashboard Component
 *
 * Main dashboard for Financial Advisors with three key sections:
 * - My Clients: Connected clients and invitation management
 * - My Network: Favorited service providers
 * - My Resources: Educational content to share with clients
 *
 * Also includes referral link management for client invitations
 */
export function Dashboard() {
  const navigate = useNavigate();
  // Read here as well as in MyClientsSection, which still owns the send button
  // and its modal. The hook only remembers an unlock in sessionStorage, so two
  // readers write the same value and neither can see a different answer.
  const showLongevity = useLongevityFlag();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    searchParams.get('fp_qa_engagement') ? 'qa' : 'clients'
  );

  useEffect(() => {
    if (searchParams.get('fp_qa_engagement')) {
      setActiveTab('qa');
    }
  }, [searchParams]);

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<FeaturedResource | null>(null);

  // const handleShareProvider = (provider: NetworkProvider) => {
  //   console.log('Share provider:', provider);
  //   // Future: Implement share provider functionality
  // };

  const handleShareResource = (resource: FeaturedResource) => {
    setSelectedResource(resource);
    setShareDialogOpen(true);
  };

  const handleFindProviders = () => {
    navigate('/search-providers');
  };

  const handleBrowseResources = () => {
    navigate('/resources');
  };

  const handleInviteSuccess = () => {
    // Refresh both clients and invitations lists (unified key pattern)
    queryClient.invalidateQueries({ queryKey: ['clients'] });
    queryClient.invalidateQueries({ queryKey: ['advisor', 'invitations', 'pending'] });
    // Future: Show toast notification
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Dashboard Header */}
        <div className="mb-6">
          <h1 className="font-recoleta-bold text-[32px] leading-tight text-text-primary">
            Financial Advisor Dashboard
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            Welcome to your Financial Advisor dashboard. Manage your clients, network, and resources
            all in one place.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <ScrollableTabsList
            containerClassName="mb-8"
            className="h-auto w-full justify-start gap-6 rounded-none border-b border-border-subtle bg-transparent p-0 text-text-secondary"
          >
            {(
              [
                { value: 'clients', label: 'Clients' },
                { value: 'network', label: 'Network' },
                { value: 'resources', label: 'Resources' },
                { value: 'qa', label: 'Care Guides' },
                { value: 'cost-calculator', label: 'Cost Calculator' },
                { value: 'profile', label: 'Profile' },
              ] as const
            ).map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="-mb-px rounded-none border-b-2 border-transparent bg-transparent px-0 py-2.5 text-sm font-medium text-text-secondary shadow-none transition-colors duration-150 ease-out hover:border-text-tertiary hover:text-text-primary data-[state=active]:border-sage data-[state=active]:bg-transparent data-[state=active]:text-text-primary data-[state=active]:shadow-none"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </ScrollableTabsList>

          <TabsContent value="clients">
            <PermissionGate permission="WORKSPACE_OPERATIONS" feature="clients">
              <MyClientsSection onInviteClick={() => setIsInviteModalOpen(true)} />
              {/* Beside the client list rather than inside it (Feature 118). It reads as
                  part of My Clients and is placed to, but it answers its own endpoints —
                  nesting it meant a failure of the clients query stopped an advisor
                  starting one of their own test runs. */}
              {showLongevity && <LongevityAssessmentsSection />}
              {/* Feature 106 — sits between clients and marketing resources: it is a thing you
                  do for a specific client, so it belongs next to the client list. */}
              <CareConciergeCard />
              <CareConciergeCases />
              <MarketingResourcesCard />
            </PermissionGate>
          </TabsContent>

          <TabsContent value="network">
            <PermissionGate permission="WORKSPACE_OPERATIONS" feature="network">
              <MyNetworkSection onFindProvidersClick={handleFindProviders} />
              <CuratedProvidersCard />
            </PermissionGate>
          </TabsContent>

          <TabsContent value="resources">
            <PermissionGate permission="WORKSPACE_OPERATIONS" feature="resources">
              <MyResourcesSection
                onShareClick={handleShareResource}
                onBrowseClick={handleBrowseResources}
              />
              <CuratedResourcesCard onShareClick={handleShareResource} />
            </PermissionGate>
          </TabsContent>

          <TabsContent value="qa">
            <QATab />
          </TabsContent>

          <TabsContent value="cost-calculator">
            <CostCalculatorTab />
          </TabsContent>

          <TabsContent value="profile">
            <ProfileTabContent />
          </TabsContent>
        </Tabs>

        {/* Invite Client Modal */}
        <InviteClientModal
          open={isInviteModalOpen}
          onOpenChange={setIsInviteModalOpen}
          onSuccess={handleInviteSuccess}
        />

        {/* Share Resource Dialog */}
        {selectedResource && (
          <ShareDialog
            open={shareDialogOpen}
            onOpenChange={setShareDialogOpen}
            shareUrl={`${window.location.origin}${selectedResource.url}`}
            resourceId={selectedResource.id}
            resourceTitle={selectedResource.title}
          />
        )}
      </div>
    </div>
  );
}
